import "@fontsource-variable/noto-kufi-arabic";
import "@fontsource-variable/ibm-plex-sans";
import "@fontsource/ibm-plex-mono/latin-400.css";
import "@fontsource/ibm-plex-mono/latin-500.css";
import "@fontsource/ibm-plex-mono/latin-600.css";
import {
  Bell,
  BookOpen,
  ChevronLeft,
  CircleHelp,
  Command,
  Languages,
  LayoutDashboard,
  Menu,
  Play,
  Search,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import {
  createContext,
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AppSurface, Locale, PreviewPreferences, ThemeId } from "@tradvera/contracts";

const defaults: PreviewPreferences = {
  locale: "ar",
  theme: "abyss",
  ratio: "65/35",
  reducedMotion: false,
};

type PreviewContextValue = {
  preferences: PreviewPreferences;
  setLocale: (locale: Locale) => void;
  setTheme: (theme: ThemeId) => void;
  patchPreferences: (patch: Partial<PreviewPreferences>) => void;
};

const PreviewContext = createContext<PreviewContextValue | null>(null);

export const themes: Array<{ id: ThemeId; ar: string; en: string; swatch: string }> = [
  { id: "abyss", ar: "مرصد الأعماق", en: "Abyss Observatory", swatch: "#53d4c1" },
  { id: "editorial", ar: "التحرير الذكي", en: "Editorial Intelligence", swatch: "#d9a66f" },
  { id: "flight", ar: "سطح القيادة", en: "Modular Flight Deck", swatch: "#7e8cff" },
  { id: "aurora", ar: "الشفق الحي", en: "Living Aurora", swatch: "#de78b5" },
];

export function PreviewProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<PreviewPreferences>(() => {
    try {
      const saved = localStorage.getItem("tradvera.preview.preferences");
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    localStorage.setItem("tradvera.preview.preferences", JSON.stringify(preferences));
    document.documentElement.lang = preferences.locale;
    document.documentElement.dir = preferences.locale === "ar" ? "rtl" : "ltr";
    document.documentElement.dataset.theme = preferences.theme;
  }, [preferences]);

  const value = useMemo<PreviewContextValue>(
    () => ({
      preferences,
      setLocale: (locale) => setPreferences((current) => ({ ...current, locale })),
      setTheme: (theme) => setPreferences((current) => ({ ...current, theme })),
      patchPreferences: (patch) => setPreferences((current) => ({ ...current, ...patch })),
    }),
    [preferences],
  );

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
}

export function usePreview() {
  const value = useContext(PreviewContext);
  if (!value) throw new Error("usePreview must be used inside PreviewProvider");
  return value;
}

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="brand" aria-label="Tradvera">
      <img
        className={compact ? "brand-mark-image" : "brand-lockup-image"}
        src={compact ? "/brand/tradvera-mark.svg" : "/brand/tradvera-lockup.svg"}
        alt={compact ? "" : "Tradvera — Learn. Decide. Trade."}
        aria-hidden={compact ? "true" : undefined}
      />
    </div>
  );
}

export function Bilingual({ ar, en, as = "span" }: { ar: string; en: string; as?: "span" | "div" }) {
  const Tag = as;
  const { preferences } = usePreview();
  return (
    <Tag className="bilingual">
      <span lang={preferences.locale} dir={preferences.locale === "ar" ? "rtl" : "ltr"}>{preferences.locale === "ar" ? ar : en}</span>
      <small lang={preferences.locale === "ar" ? "en" : "ar"} dir={preferences.locale === "ar" ? "ltr" : "rtl"}>{preferences.locale === "ar" ? en : ar}</small>
    </Tag>
  );
}

export function StatusPill({ tone = "neutral", children }: { tone?: "teal" | "amber" | "coral" | "violet" | "neutral"; children: ReactNode }) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

export function PreviewBanner() {
  return (
    <div className="preview-banner" role="status">
      <span><Sparkles size={13} /> FOUNDER PREVIEW</span>
      <span className="preview-separator" />
      <span>البيانات الفعلية موسومة · ACTUAL DATA WHERE MARKED</span>
      <span className="preview-spacer" />
      <span>لا أوامر تداول · NO EXECUTION</span>
    </div>
  );
}

export function ThemeDock({ placement = "floating" }: { placement?: "floating" | "rail" }) {
  const { preferences, setTheme } = usePreview();
  return (
    <div className={`theme-dock is-${placement}`} aria-label="ألوان الصفحة">
      <span className="theme-dock-label">ألوان الصفحة</span>
      {themes.map((theme) => (
        <button
          className={preferences.theme === theme.id ? "theme-choice is-active" : "theme-choice"}
          key={theme.id}
          onClick={() => setTheme(theme.id)}
          aria-label={`${theme.ar} — ${theme.en}`}
          title={`${theme.ar} — ${theme.en}`}
        >
          <span style={{ "--swatch": theme.swatch } as CSSProperties} />
          <small>{theme.en.split(" ")[0]}</small>
        </button>
      ))}
    </div>
  );
}

type NavItem = { id: string; ar: string; en: string; icon: ReactNode };

const shellNav: Record<AppSurface, NavItem[]> = {
  portal: [
    { id: "home", ar: "العبور", en: "Portal", icon: <LayoutDashboard size={18} /> },
    { id: "academy", ar: "الأكاديمية", en: "Academy", icon: <BookOpen size={18} /> },
    { id: "trade", ar: "التداول", en: "Trade", icon: <Command size={18} /> },
    { id: "bridge", ar: "المزدوج", en: "Bridge", icon: <ShieldCheck size={18} /> },
  ],
  bridge: [], academy: [], trade: [],
};

export function AppShell({
  surface,
  children,
  active = "home",
  onNavigate,
  title,
  eyebrow,
  account,
  guideSurface,
}: {
  surface: AppSurface;
  children: ReactNode;
  active?: string;
  onNavigate?: (id: string) => void;
  title?: string;
  eyebrow?: string;
  account?: ReactNode;
  guideSurface?: "academy" | "trade";
}) {
  const { preferences, setLocale } = usePreview();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [explainActive, setExplainActive] = useState(false);
  const [foxActive, setFoxActive] = useState(false);
  const nav = surface === "portal" ? shellNav.portal : [];
  const activeGuideSurface = guideSurface ?? (surface === "academy" || surface === "trade" ? surface : undefined);

  useEffect(() => {
    const syncExplainState = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
      setExplainActive(Boolean(detail?.enabled));
    };
    const syncFoxState = (event: Event) => {
      const detail = (event as CustomEvent<{ active?: boolean; surface?: "academy" | "trade" }>).detail;
      if (detail?.surface === activeGuideSurface) setFoxActive(Boolean(detail.active));
    };
    window.addEventListener("tradvera:explain-state", syncExplainState);
    window.addEventListener("tradvera:fox-state", syncFoxState);
    return () => {
      window.removeEventListener("tradvera:explain-state", syncExplainState);
      window.removeEventListener("tradvera:fox-state", syncFoxState);
    };
  }, [activeGuideSurface]);

  useEffect(() => {
    if (activeGuideSurface !== "trade") setExplainActive(false);
    setFoxActive(false);
  }, [activeGuideSurface]);

  return (
    <div className={`app-frame surface-${surface}`}>
      <PreviewBanner />
      <header className="topbar">
        <button className="icon-button mobile-menu" onClick={() => setMobileOpen(true)} aria-label="فتح القائمة"><Menu size={20} /></button>
        <Brand />
        {(title || eyebrow) && (
          <div className="topbar-context">
            <small>{eyebrow}</small><span>{title}</span>
          </div>
        )}
        <div className="topbar-actions">
          <button className="command-trigger" aria-label="البحث والأوامر" onClick={() => window.dispatchEvent(new Event("tradvera:open-search"))}><Search size={16} /><span>بحث أو أمر</span><kbd>⌘ K</kbd></button>
          <button className="icon-button notification-button" aria-label="التنبيهات" onClick={() => window.location.assign("/companion/?tab=inbox")}><Bell size={18} /><i /></button>
          <button className="language-toggle" onClick={() => setLocale(preferences.locale === "ar" ? "en" : "ar")}>
            <Languages size={17} /><span>{preferences.locale === "ar" ? "EN" : "ع"}</span>
          </button>
          {account ?? <div className="avatar" aria-label="وضع المعاينة">ت</div>}
        </div>
      </header>

      {nav.length > 0 && (
        <aside className={mobileOpen ? "rail mobile-is-open" : "rail"}>
          <button className="icon-button rail-close" onClick={() => setMobileOpen(false)} aria-label="إغلاق القائمة"><X size={18} /></button>
          <span className="rail-index">TV / 01</span>
          <nav>
            {nav.map((item, index) => (
              <button
                key={item.id}
                className={active === item.id ? "rail-link is-active" : "rail-link"}
                onClick={() => { onNavigate?.(item.id); setMobileOpen(false); }}
              >
                <span className="rail-number">0{index + 1}</span>
                {item.icon}
                <span><b>{item.ar}</b><small>{item.en}</small></span>
                <ChevronLeft size={14} className="rail-arrow" />
              </button>
            ))}
          </nav>
          {activeGuideSurface && (
            <div className="rail-tools" aria-label={preferences.locale === "ar" ? "أدوات المساعدة" : "Help tools"}>
              <span className="rail-tools-title">{preferences.locale === "ar" ? "المساعدة" : "ASSIST"}</span>
              {activeGuideSurface === "trade" && (
                <button
                  type="button"
                  className={explainActive ? "rail-link rail-tool rail-tool-explain is-active" : "rail-link rail-tool rail-tool-explain"}
                  aria-pressed={explainActive}
                  onClick={() => {
                    window.dispatchEvent(new Event("tradvera:explain-toggle"));
                    setMobileOpen(false);
                  }}
                >
                  <span className="rail-number">05</span>
                  <CircleHelp size={18} />
                  <span><b>{preferences.locale === "ar" ? (explainActive ? "أغلق افهمني" : "افهمني") : (explainActive ? "Exit Explain" : "Explain")}</b><small>{preferences.locale === "ar" ? "EXPLAIN MODE" : "وضع الشرح"}</small></span>
                  <span className={explainActive ? "rail-tool-state is-on" : "rail-tool-state"} aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                className={foxActive ? "rail-link rail-tool rail-tool-fox is-active" : "rail-link rail-tool rail-tool-fox"}
                aria-pressed={foxActive}
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("tradvera:fox-start", { detail: { surface: activeGuideSurface } }));
                  setMobileOpen(false);
                }}
              >
                <span className="rail-number">{activeGuideSurface === "trade" ? "06" : "05"}</span>
                <FoxMark />
                <span><b>{preferences.locale === "ar" ? "جولة فوكس" : "Fox Tour"}</b><small>{preferences.locale === "ar" ? "ONBOARDING" : "جولة تعريفية"}</small></span>
                <Play size={13} className="rail-tool-play" />
              </button>
            </div>
          )}
          <div className="rail-foot">
            <span className="live-dot" />
            <span>PREVIEW SYSTEM</span>
            <small>v0.1</small>
          </div>
          <ThemeDock placement="rail" />
        </aside>
      )}

      <main className={nav.length ? "app-main with-rail" : "app-main"}>{children}</main>
      {activeGuideSurface && <FoxOnboarding surface={activeGuideSurface} hideLauncher={nav.length > 0} />}
      {nav.length === 0 && <ThemeDock />}
    </div>
  );
}

type FoxTourStep = { target: string; eyebrow: string; title: string; titleEn: string; body: string; bodyEn: string };

const foxTours: Record<"academy" | "trade", FoxTourStep[]> = {
  academy: [
    { target: ".academy-mast", eyebrow: "ACADEMY / 01", title: "أهلًا، أنا فوكس", titleEn: "Hi, I’m Fox", body: "هعرفك على أكاديمية Tradvera من نفس الشاشة. هنا هتشوف حالة المنهج والتقدم، من غير ما أغيّر أي إجابة أو نتيجة.", bodyEn: "I’ll guide you through Tradvera Academy on the real screen without changing an answer or result." },
    { target: ".academy-workspace > .workspace-tabs", eyebrow: "ACADEMY / 02", title: "كل رحلة ليها مكان", titleEn: "Every journey has a place", body: "رحلتي للدراسة اليومية، الخريطة للتنقل، المدرّب للنقاش، ومصنع الدورات يحوّل كتبك إلى مسار مستقل قابل للمراجعة.", bodyEn: "Learning Path is for daily study, Map for navigation, Tutor for discussion, and Course Foundry turns books into reviewable programs." },
    { target: ".learning-map", eyebrow: "ACADEMY / 03", title: "ابدأ من الدليل التالي", titleEn: "Start with the next evidence", body: "المسار يفتح الدرس أو بوابة المستوى التالية فقط لما تحقق شروطها. الزر هنا هو أقصر طريق للخطوة الصحيحة.", bodyEn: "The path unlocks the next lesson or level gate only when its conditions are met." },
    { target: ".learning-rail", eyebrow: "ACADEMY / 04", title: "تقدمك مش مجرد نسبة", titleEn: "Progress is more than a percentage", body: "هنا تشوف الدروس والبوابات والاحتفاظ النهائي منفصلين، عشان الإتقان مايتحولش لرقم تجميلي.", bodyEn: "Lessons, gates, and final retention stay separate so mastery never becomes a decorative score." },
    { target: ".foundry-invite", eyebrow: "ACADEMY / 05", title: "كتبك تتحول لدورات", titleEn: "Turn books into courses", body: "من هنا تضيف مصدرًا جديدًا. الوكلاء يفحصون الحق، يستخرجون المحتوى ويقترحون هيكلًا، لكن النشر النهائي يظل بمراجعتك.", bodyEn: "Add a source here. Agents check rights, extract content, and propose structure; final publication remains under your review." },
  ],
  trade: [
    { target: ".trade-mast", eyebrow: "TRADE / 01", title: "أهلًا، أنا فوكس", titleEn: "Hi, I’m Fox", body: "هفهمك Trade كرحلة قرار: دليل أولًا، فرضية بعده، مراجعة مخاطر، وفي الآخر قرار موثق أو امتناع.", bodyEn: "I’ll guide you through Trade as a decision journey: evidence, thesis, risk review, then a documented decision or abstention." },
    { target: ".trade-workspace > .workspace-tabs", eyebrow: "TRADE / 02", title: "ست محطات مستقلة", titleEn: "Six independent stations", body: "مركز القيادة يلخص، سجل الأدلة يثبت، الإنتاج يختبر البنية، المحاور تنظم Paper، ثم الفرصة والقرار.", bodyEn: "Command summarizes, Evidence verifies, Production tests the spine, Themes organizes Paper, followed by Opportunity and Decision." },
    { target: ".signal-field", eyebrow: "TRADE / 03", title: "دي خريطة الحالة، مش إشارة", titleEn: "A state map, not a signal", body: "الرقم في المنتصف يجمع حالة الدليل والفرضية والمخاطر. لا هو احتمال ربح ولا أمر شراء.", bodyEn: "The center combines evidence, thesis, and risk state. It is neither profit probability nor a buy order." },
    { target: ".command-rail", eyebrow: "TRADE / 04", title: "الفرضية لها عمر وحدود", titleEn: "A thesis has a life and limits", body: "هنا الصلاحية وميزان الأدلة وRisk Gate. أي نقص يفضل ظاهر بدل ما يتغطى بقصة مقنعة.", bodyEn: "Validity, evidence balance, and the Risk Gate stay visible so missing evidence cannot hide behind a convincing story." },
    { target: ".event-cascade", eyebrow: "TRADE / 05", title: "من الخبر للأثر خطوة بخطوة", titleEn: "From news to impact, step by step", body: "فوكس هيفكرك إن كل انتقال من شركة لمورد أو فرصة محتاج Claim ومصدر، ومفيش خبر يتحول تلقائيًا لصفقة.", bodyEn: "Every move from a company to a supplier or opportunity needs a claim and source; news never becomes a trade automatically." },
    { target: ".rail-tool-explain, .trade-explain-toggle", eyebrow: "TRADE / 06", title: "وأي حاجة تقف قدامك: افهمني", titleEn: "When anything is unclear: Explain", body: "شغّل وضع افهمني واضغط على أي رقم أو أداة. هتلاقي المعنى الحالي، الحساب، الحدود، ومثال، وتقدر تسأل ميار عن نفس العنصر.", bodyEn: "Turn on Explain Mode and select any number or tool for its current meaning, calculation, limits, example, and a contextual question to Mayar." },
  ],
};

export function FoxOnboarding({ surface, hideLauncher = false }: { surface: "academy" | "trade"; hideLauncher?: boolean }) {
  const { preferences } = usePreview();
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const targetRef = useRef<Element | null>(null);
  const steps = foxTours[surface];

  useEffect(() => {
    if (!active) return;
    const update = () => {
      const target = document.querySelector(steps[index]?.target ?? "");
      targetRef.current = target;
      if (!target) {
        setRect(null);
        return;
      }
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      window.setTimeout(() => setRect(target.getBoundingClientRect()), 220);
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, index, steps]);

  const move = (direction: 1 | -1) => {
    let candidate = index + direction;
    while (candidate >= 0 && candidate < steps.length && !document.querySelector(steps[candidate].target)) candidate += direction;
    if (candidate < 0 || candidate >= steps.length) {
      if (direction === 1) setActive(false);
      return;
    }
    setIndex(candidate);
  };

  const start = () => {
    const firstAvailable = steps.findIndex((step) => document.querySelector(step.target));
    setIndex(firstAvailable < 0 ? 0 : firstAvailable);
    setActive(true);
  };

  useEffect(() => {
    const handleExternalStart = (event: Event) => {
      const detail = (event as CustomEvent<{ surface?: "academy" | "trade" }>).detail;
      if (detail?.surface === surface) start();
    };
    window.addEventListener("tradvera:fox-start", handleExternalStart);
    return () => window.removeEventListener("tradvera:fox-start", handleExternalStart);
  });

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("tradvera:fox-state", { detail: { surface, active } }));
    return () => {
      if (active) window.dispatchEvent(new CustomEvent("tradvera:fox-state", { detail: { surface, active: false } }));
    };
  }, [active, surface]);

  const step = steps[index];
  const englishFirst = preferences.locale === "en";
  return <>
    {!hideLauncher && <button type="button" className="fox-guide-launcher" onClick={start} aria-label={`ابدأ جولة فوكس في ${surface === "academy" ? "الأكاديمية" : "التداول"}`}>
      <FoxMark/><span>{englishFirst ? "Fox Tour" : "جولة فوكس"}</span><small>{englishFirst ? "جولة تعريفية" : "ONBOARDING"}</small><Play size={12}/>
    </button>}
    {active && <div className="fox-tour-layer" role="dialog" aria-modal="true" aria-label="جولة فوكس التعريفية">
      {rect && <div className="fox-tour-spotlight" style={{ top: Math.max(7, rect.top - 7), left: Math.max(7, rect.left - 7), width: Math.min(window.innerWidth - 14, rect.width + 14), height: Math.min(window.innerHeight - 14, rect.height + 14) }}/>}
      <section className={rect && rect.top > window.innerHeight * .48 ? "fox-tour-card is-top" : "fox-tour-card"}>
        <button className="fox-tour-close" onClick={() => setActive(false)} aria-label="إنهاء الجولة"><X size={17}/></button>
        <FoxMark large/>
        <div className="fox-tour-copy"><small>{step.eyebrow}</small><h2>{englishFirst ? step.titleEn : step.title}</h2><span>{englishFirst ? step.title : step.titleEn}</span><p>{rect ? (englishFirst ? step.bodyEn : step.body) : (englishFirst ? "This section is not visible in the current view. Open it from the tabs and restart Fox Tour at any time." : "القسم ده مش ظاهر في الشاشة الحالية. تقدر تفتحه من التبويبات وتشغّل جولة فوكس في أي وقت.")}</p><em>{rect ? (englishFirst ? step.body : step.bodyEn) : "ON-DEMAND ONBOARDING · جولة عند الطلب"}</em></div>
        <div className="fox-tour-progress" aria-label={`الخطوة ${index + 1} من ${steps.length}`}>{steps.map((_, itemIndex) => <i key={itemIndex} className={itemIndex <= index ? "is-done" : ""}/>)}</div>
        <div className="fox-tour-actions"><button disabled={index === 0} onClick={() => move(-1)}>{englishFirst ? "Previous" : "السابق"}<small>{englishFirst ? "السابق" : "PREVIOUS"}</small></button><span>{index + 1} / {steps.length}</span><button className="is-primary" onClick={() => move(1)}>{englishFirst ? (index === steps.length - 1 ? "Start" : "Next") : (index === steps.length - 1 ? "ابدأ الاستخدام" : "التالي")}<small>{englishFirst ? (index === steps.length - 1 ? "ابدأ" : "التالي") : (index === steps.length - 1 ? "START" : "NEXT")}</small></button></div>
      </section>
    </div>}
  </>;
}

function FoxMark({ large = false }: { large?: boolean }) {
  return <svg className={large ? "fox-mark is-large" : "fox-mark"} viewBox="0 0 64 64" role="img" aria-label="فوكس مرشد Tradvera">
    <path d="M9 11l16 8h14l16-8-5 22c-2 13-9 21-18 24-9-3-16-11-18-24L9 11z" fill="currentColor" opacity=".16"/>
    <path d="M10 12l17 12m27-12L37 24M10 12l5 24 17 20 17-20 5-24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round"/>
    <path d="M18 22l9 3-7 10m26-13l-9 3 7 10M24 42l8 6 8-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="24" cy="34" r="2" fill="currentColor"/><circle cx="40" cy="34" r="2" fill="currentColor"/>
    <path d="M29 41h6l-3 4-3-4z" fill="var(--amber)"/>
  </svg>;
}

export function SectionHeading({ index, title, subtitle, action }: { index?: string; title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="section-heading">
      <div>{index && <span className="section-index">{index}</span>}<h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
      {action}
    </div>
  );
}

export function ProgressRing({ value, label, size = 94 }: { value: number; label: string; size?: number }) {
  return (
    <div className="progress-ring" style={{ "--progress": `${value * 3.6}deg`, "--ring-size": `${size}px` } as CSSProperties}>
      <div><strong>{value}%</strong><small>{label}</small></div>
    </div>
  );
}

export function EdgeButton({ children, tone = "primary", onClick, icon, disabled = false }: { children: ReactNode; tone?: "primary" | "ghost" | "danger"; onClick?: () => void; icon?: ReactNode; disabled?: boolean }) {
  return <button className={`edge-button edge-${tone}`} onClick={onClick} disabled={disabled}>{children}{icon}</button>;
}

export { Bell, BookOpen, Command, Search, ShieldCheck, Sparkles };
